import {
  useRef, useState, useEffect, ReactNode,
} from 'react';
import { useSelector } from 'react-redux';
import ParsedMarkdown from './ParsedMarkdown';
import UniversalLinkContainer from './UniversalLink';
import { getQueryParams } from '../../store/selectors/router.selectors';
import { scrollToAnchor } from '../../common/WithScrollingView';
import { getScrollHandlingComponentsAnchor } from '../../store/selectors/ui.selectors';
import { FONT_VARIANT_CLASS_NAME } from '../../../../consts/consts';
import { generateCenteredColumnSpanClassNames } from '../../../../utils/Utils';
import { withCssModulesClassNames } from '../../common/nextMigrationHelpers';

import { default as styles } from '../../scss/components/Accordion.module.scss';
import { BodyFontSize } from '../../types/schema';
import { FooterNavigationItem, FooterPoint } from './footer/footer.types';
import { Point } from './header/header.types';
import { Nullable } from '../../types/common';

const withCssModulesClassNamesHandler = withCssModulesClassNames(styles);

type AccordionPoints = FooterPoint | Point;

export type AccordionProps = {
  additionalClassName?: string;
  headerClassName?: string;
  iconCloseClassName?: string;
  id?: string;
  itemActionClassName?: string;
  itemBodyClassName?: string;
  itemClassName?: string;
  itemWrapperClassName?: string;
  bodyFontSize?: `${BodyFontSize}`;
  points: (Partial<AccordionPoints>)[] | [];
  columnWidths?: string[];
  renderItemBody?: (props: FooterNavigationItem)=> ReactNode;
  anchor?: string;
  disclaimer?: string;
  header?: string;
};

export const parseQueryParam = (queryParam: string, points: AccordionProps['points']) => {
  let index = -1;
  const isOfTypePoint = (arg: AccordionProps['points']): arg is Point[] => Array.isArray(arg) && arg.every((argItem) => 'anchor' in argItem);
  if (isOfTypePoint(points)) {
    index = points.findIndex(({ anchor: pointAnchor }) => pointAnchor === queryParam);
  }

  return !Number.isNaN(+queryParam)
    ? +queryParam - 1 // subtracting 1 so we could use human indexing in URL
    : index;
};

export const getInitiallyOpenSections = (anchor: AccordionProps['anchor'], queryParams: { [x: string]: unknown; }, points: AccordionProps['points']) => {
  if (!(anchor && queryParams?.[anchor])) {
    return [];
  }
  const params = queryParams[anchor];

  const initiallyOpen = typeof params === 'string'
    ? [parseQueryParam(params, points)]
    : (Array.isArray(params) ? params : []).map((individualParam) => parseQueryParam(individualParam, points));

  return initiallyOpen
    .filter((index) => index >= 0)
    .sort((a, b) => a - b);
};

const Accordion = (props: AccordionProps) => {
  const {
    bodyFontSize,
    id,
    anchor,
    header,
    points = [],
    disclaimer = '',
    columnWidths = [],
    itemClassName = '',
    headerClassName = '',
    itemBodyClassName = '',
    iconCloseClassName = '',
    additionalClassName = '',
    itemActionClassName = '',
    renderItemBody = () => null,
    itemWrapperClassName = '',
  } = props;

  const [open, setOpen] = useState<number[]>([]);
  const containerInnerWrapperRef = useRef<Nullable<HTMLDivElement>>(null);
  const requestRef = useRef<Nullable<number>>(null);
  const scrollHandlingAnchor: string | undefined = useSelector(getScrollHandlingComponentsAnchor);
  const queryParams: { [x: string]: unknown; } = useSelector(getQueryParams);

  const shouldHandleScroll = () => anchor && scrollHandlingAnchor === anchor;

  useEffect(() => {
    const initiallyOpen = getInitiallyOpenSections(anchor, queryParams, points);
    const shouldHandleScrollCheck = shouldHandleScroll();
    setOpen(initiallyOpen);

    const handleScrollToAnchor = () => {
      const scrollToTarget = containerInnerWrapperRef?.current?.children[initiallyOpen[0]] || containerInnerWrapperRef.current;
      scrollToAnchor(scrollToTarget);
    };

    if (shouldHandleScrollCheck) {
      requestRef.current = requestAnimationFrame(handleScrollToAnchor);
    }

    return () => cancelAnimationFrame(requestRef.current ? requestRef.current : 0);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleOpen = (index: number) => {
    const nextOpen = open.includes(index)
      ? open.filter((x) => x !== index)
      : [...open, index];
    setOpen(nextOpen);
  };

  const renderItem = (item: AccordionProps['points'][0], index: number) => {
    const itemHeader = item.header;
    let body;
    let itemId;
    let links;
    let markdownCopy;
    let copy;

    if ('body' in item) {
      ({ body, id: itemId } = item);
    } else if ('copy' in item) {
      ({
        markdownCopy,
        copy,
        links,
      } = item);
    }
    const [link] = links || [];
    const fontSizeClassName = FONT_VARIANT_CLASS_NAME[bodyFontSize || 'XL'];
    return (
      <div
        key={itemId || index}
        className={withCssModulesClassNamesHandler(
          'accordion__item',
          itemClassName,
          {
            'accordion__item--open': open.includes(index),
          },
        )}
      >
        <h3
          className={withCssModulesClassNamesHandler(
            'accordion__item-title',
            headerClassName,
          )}
        >
          <button
            data-testid="accordion-button"
            onClick={() => toggleOpen(index)}
            id={`accordion-header-${id}-${index}`}
            aria-expanded={open.includes(index)}
            aria-controls={`accordion-panel-${id}-${index}`}
          >
            {itemHeader}
            <span
              className={withCssModulesClassNamesHandler(
                'accordion__item-actions',
                itemActionClassName,
              )}
            >
              <i className={
                withCssModulesClassNamesHandler(
                  'pointer',
                  'icon',
                  'icon-close',
                  iconCloseClassName,
                )}
              />
            </span>
          </button>
        </h3>
        <div
          id={`accordion-panel-${id}-${index}`}
          aria-labelledby={`accordion-header-${id}-${index}`}
          className={withCssModulesClassNamesHandler(
            'accordion__item-body',
            itemBodyClassName,
            fontSizeClassName,
          )}
          hidden={!open.includes(index)}
          data-testid="accordion-panel"
        >
          {copy}
          {markdownCopy && (
            <ParsedMarkdown
              options={{
                forceBlock: true,
                overrides: {
                  p: {
                    props: {
                      className: fontSizeClassName,
                    },
                  },
                  li: {
                    props: {
                      className: fontSizeClassName,
                    },
                  },
                },
              }}
            >
              {markdownCopy}
            </ParsedMarkdown>
          )}
          {renderItemBody && body && renderItemBody(body)}
          {link && (
            <UniversalLinkContainer
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...link}
              className={withCssModulesClassNamesHandler('cta-link', link.className, fontSizeClassName)}
            >
              {link.text}
            </UniversalLinkContainer>
          )}
        </div>
      </div>
    );
  };

  if (points?.length === 0) return null;

  const columnWidthsClassNames = generateCenteredColumnSpanClassNames(columnWidths).split(' ');

  return (
    <div id={anchor} className={withCssModulesClassNamesHandler('accordion', additionalClassName)}>
    <div
      ref={containerInnerWrapperRef}
      className={withCssModulesClassNamesHandler(
        itemWrapperClassName,
        'accordion-inner-wrapper',
        columnWidthsClassNames,
      )}
    >
      {header && <h2 className={withCssModulesClassNamesHandler('accordion__header')}>{header}</h2>}
      {points?.map(renderItem)}
      {disclaimer && <p className={withCssModulesClassNamesHandler('accordion__disclaimer', FONT_VARIANT_CLASS_NAME.M)}>{disclaimer}</p>}
    </div>
  </div>
  );
};

export default Accordion;
